/**
 * TevredenheidPage — public NPS survey page.
 * /tevredenheid/:token — no login required.
 * Multi-step: NPS score -> Category scores -> Feedback -> Thank you.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, Send, CheckCircle, Loader2 } from 'lucide-react';
import clsx from 'clsx';

interface SurveyData {
  klantNaam: string;
  winkelNaam: string;
  dossierNummer: string;
  status: string;
  ingevuldOp: string | null;
}

const CATEGORIEN = [
  { key: 'opmeting', label: 'Opmeting' },
  { key: 'levering', label: 'Levering' },
  { key: 'plaatsing', label: 'Plaatsing' },
  { key: 'communicatie', label: 'Communicatie' },
  { key: 'netheid', label: 'Netheid' },
];

const NPS_EMOJIS = ['😡', '😠', '😤', '😟', '😕', '😐', '🙂', '😊', '😄', '😍', '🤩'];

type Step = 'nps' | 'categories' | 'feedback' | 'done';

export function TevredenheidPage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SurveyData | null>(null);
  const [step, setStep] = useState<Step>('nps');
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [npsScore, setNpsScore] = useState<number | null>(null);
  const [catScores, setCatScores] = useState<Record<string, number>>({});
  const [feedback, setFeedback] = useState('');
  const [aanbeveling, setAanbeveling] = useState<boolean | null>(null);
  const [verbeterpunten, setVerbeterpunten] = useState('');

  useEffect(() => {
    if (!token) return;
    fetch(`/api/tevredenheid/${token}`)
      .then(r => {
        if (!r.ok) throw new Error('Onderzoek niet gevonden');
        return r.json();
      })
      .then(d => {
        setData(d);
        if (d.status === 'INGEVULD') setStep('done');
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    if (npsScore === null) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/tevredenheid/${token}/invullen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          npsScore,
          categorieScores: Object.keys(catScores).length > 0 ? catScores : undefined,
          feedback: feedback || undefined,
          aanbeveling,
          verbeterpunten: verbeterpunten || undefined,
        }),
      });
      if (!res.ok) throw new Error('Verzenden mislukt');
      setStep('done');
    } catch {
      setError('Er ging iets mis bij het verzenden. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-brand" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <p className="text-6xl mb-4">🔗</p>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Link niet gevonden</h1>
          <p className="text-gray-500 text-sm">{error || 'Dit onderzoek is niet beschikbaar.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-brand-secondary text-white py-6 px-4 text-center">
        <div className="w-10 h-10 rounded-xl bg-brand flex items-center justify-center mx-auto mb-3 text-lg font-bold">
          C
        </div>
        <h1 className="text-lg font-bold">{data.winkelNaam}</h1>
        <p className="text-sm text-gray-400 mt-1">Tevredenheidsonderzoek</p>
      </div>

      <div className="max-w-md mx-auto p-4 pb-12">
        {/* Greeting */}
        {step !== 'done' && (
          <div className="bg-white rounded-2xl p-5 shadow-sm mb-4 text-center">
            <p className="text-sm text-gray-600">
              Beste <strong>{data.klantNaam}</strong>,
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Hoe tevreden bent u over onze dienstverlening? Uw feedback helpt ons te verbeteren.
            </p>
          </div>
        )}

        {/* Step indicators */}
        {step !== 'done' && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {(['nps', 'categories', 'feedback'] as const).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={clsx(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors',
                  step === s ? 'bg-brand text-white' :
                  (['nps', 'categories', 'feedback'].indexOf(step) > i) ? 'bg-green-500 text-white' :
                  'bg-gray-200 text-gray-400',
                )}>
                  {(['nps', 'categories', 'feedback'].indexOf(step) > i) ? '✓' : i + 1}
                </div>
                {i < 2 && <div className="w-8 h-0.5 bg-gray-200" />}
              </div>
            ))}
          </div>
        )}

        {/* ═══ STEP 1: NPS Score ═══ */}
        {step === 'nps' && (
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h2 className="text-base font-bold text-gray-900 mb-1 text-center">
              Hoe waarschijnlijk is het dat u ons aanbeveelt?
            </h2>
            <p className="text-xs text-gray-400 text-center mb-5">0 = zeer onwaarschijnlijk, 10 = zeer waarschijnlijk</p>

            {/* Score buttons */}
            <div className="grid grid-cols-11 gap-1 mb-4">
              {Array.from({ length: 11 }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setNpsScore(i)}
                  className={clsx(
                    'aspect-square rounded-lg flex flex-col items-center justify-center text-xs font-bold transition-all active:scale-90',
                    npsScore === i
                      ? i <= 6 ? 'bg-red-500 text-white ring-2 ring-red-300'
                        : i <= 8 ? 'bg-amber-500 text-white ring-2 ring-amber-300'
                        : 'bg-green-500 text-white ring-2 ring-green-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                  )}
                >
                  {i}
                </button>
              ))}
            </div>

            {/* Selected emoji */}
            {npsScore !== null && (
              <div className="text-center mb-4">
                <span className="text-4xl">{NPS_EMOJIS[npsScore]}</span>
              </div>
            )}

            <div className="flex justify-between text-[10px] text-gray-400 mb-5">
              <span>Zeer onwaarschijnlijk</span>
              <span>Zeer waarschijnlijk</span>
            </div>

            <button
              onClick={() => setStep('categories')}
              disabled={npsScore === null}
              className="w-full py-3 bg-brand text-white rounded-xl font-bold text-sm hover:bg-[#b89a55] active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Volgende
            </button>
          </div>
        )}

        {/* ═══ STEP 2: Category Scores ═══ */}
        {step === 'categories' && (
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h2 className="text-base font-bold text-gray-900 mb-1 text-center">
              Score per categorie
            </h2>
            <p className="text-xs text-gray-400 text-center mb-5">Geef elke categorie een score van 1 tot 5</p>

            <div className="space-y-4 mb-6">
              {CATEGORIEN.map(cat => (
                <div key={cat.key}>
                  <p className="text-sm font-medium text-gray-700 mb-1.5">{cat.label}</p>
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map(s => (
                      <button
                        key={s}
                        onClick={() => setCatScores(prev => ({ ...prev, [cat.key]: s }))}
                        className="flex-1 py-2 rounded-lg transition-all active:scale-90"
                      >
                        <Star
                          size={24}
                          className={clsx(
                            'mx-auto transition-colors',
                            (catScores[cat.key] || 0) >= s
                              ? 'fill-brand text-brand'
                              : 'text-gray-300',
                          )}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setStep('nps')}
                className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 active:scale-[0.98] transition-all"
              >
                Terug
              </button>
              <button
                onClick={() => setStep('feedback')}
                className="flex-1 py-3 bg-brand text-white rounded-xl font-bold text-sm hover:bg-[#b89a55] active:scale-[0.98] transition-all"
              >
                Volgende
              </button>
            </div>
          </div>
        )}

        {/* ═══ STEP 3: Feedback ═══ */}
        {step === 'feedback' && (
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h2 className="text-base font-bold text-gray-900 mb-4 text-center">
              Uw feedback
            </h2>

            <div className="space-y-4 mb-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Zou u ons aanbevelen aan anderen?
                </label>
                <div className="flex gap-2">
                  {([true, false] as const).map(val => (
                    <button
                      key={String(val)}
                      onClick={() => setAanbeveling(val)}
                      className={clsx(
                        'flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-[0.97]',
                        aanbeveling === val
                          ? val ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                          : 'bg-gray-100 text-gray-600',
                      )}
                    >
                      {val ? 'Ja, zeker!' : 'Nee'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Wat vond u goed? (optioneel)
                </label>
                <textarea
                  value={feedback}
                  onChange={e => setFeedback(e.target.value)}
                  placeholder="Vertel ons wat u gewaardeerd heeft..."
                  className="w-full h-20 px-3 py-2 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Wat kunnen we verbeteren? (optioneel)
                </label>
                <textarea
                  value={verbeterpunten}
                  onChange={e => setVerbeterpunten(e.target.value)}
                  placeholder="Suggesties voor verbetering..."
                  className="w-full h-20 px-3 py-2 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setStep('categories')}
                className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 active:scale-[0.98] transition-all"
              >
                Terug
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-3 bg-brand text-white rounded-xl font-bold text-sm hover:bg-[#b89a55] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Verzenden
              </button>
            </div>
          </div>
        )}

        {/* ═══ STEP 4: Thank You ═══ */}
        {step === 'done' && (
          <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Bedankt!</h2>
            <p className="text-sm text-gray-500 mb-1">
              Uw feedback is ontvangen en helpt ons onze dienstverlening te verbeteren.
            </p>
            <p className="text-xs text-gray-400">
              — {data.winkelNaam}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-4">
        <p className="text-[10px] text-gray-300">Powered by Kordaro</p>
      </div>
    </div>
  );
}
